import React from "react";
import { useLocation } from "react-router-dom";
import { css, styled } from "styled-components";

interface AddressListItemProps {
    $active: boolean;
    addr: AddressDetail;
    objArr: any[];
    searchParams: any;
    setSelectedOtherField: (field: any) => void;
    onClickItem: (params: { pnu?: string; lat?: string | number; lng?: string | number; idx?: number }) => void;
    idx: number;
}

export const AddressListItem: React.FC<AddressListItemProps> = ({
    $active,
    addr,
    objArr,
    searchParams,
    setSelectedOtherField,
    onClickItem,
    idx,
}) => {
    const location = useLocation();
    const isPublicAuction = location.pathname.includes("publicAuction") || searchParams.toString().includes("publicAuction");
    const handleClick = () => {
        const [lng, lat] = objArr.find((each: any) => each.no === addr.no)?.center?.coordinates || [undefined, undefined];

        setSelectedOtherField({
            pnu: addr.pnu,
            serial: addr.sub_serial,
            itemId: searchParams.get("itemId") ?? "",
            no: addr?.no ?? "1",
            type: /(토지|land)/.test(addr?.type) ? "land" : "building",
        });

        onClickItem({
            pnu: addr.pnu,
            lat,
            lng,
            idx,
        });
    };

    return (
        <Container>
            <AddressListContainer $active={$active} onClick={handleClick}>
                <div className="listBox">
                    <p className="serial">{isPublicAuction ? addr.no : addr.sub_serial}</p>

                    <div className="useBoxStyle">
                        <p>{objArr.find((each: any) => each.no === addr.no)?.detail_type}</p>
                        <div></div>
                        <p>{objArr.find((each: any) => each.no === addr.no)?.area?.pyong}평</p>
                    </div>
                    <p className={`jibunBox ${$active ? "active" : ""}`}>
                        {addr.type == "건물" || addr.type === "집합건물" ? addr.address_jibun ?? addr.address : addr.address}
                    </p>
                </div>
                {(addr.type == "건물" || addr.type === "집합건물") && addr.address_jibun && addr.address && (
                    <>
                        <div className="addressLine" />
                        <div className="doroBox">
                            <p className="doroTitle">도로명</p>
                            {addr.address}
                        </div>
                    </>
                )}
            </AddressListContainer>
            {!isPublicAuction && (
                <div className="estPriceBox">
                    <span className="estPrice">감정가</span>
                    <span className="estPriceValue">{addr.est_price}</span>
                </div>
            )}
        </Container>
    );
};

export const groupAddressListByType = (addressList: any[]): GroupedAddressList => {
    return addressList.reduce(
        (acc: GroupedAddressList, curr) => {
            if (curr.type === "토지" || curr.type === "land") {
                acc.토지.push(curr);
            } else if (curr.type === "건물") {
                acc.건물.push(curr);
            } else if (curr.type === "집합건물") {
                acc.집합건물.push(curr);
            } else if (curr.type === "기타") {
                acc.기타.push(curr);
            }
            return acc;
        },
        { 토지: [], 건물: [], 집합건물: [], 기타: [] },
    );
};

const activeAddressStyle = css`
    background-color: #0047ff !important;
    color: #fff;

    .serial {
        background-color: #fff;
        color: #2275ee;
    }

    .useBoxStyle {
        color: #fff !important;
        border: 1px solid #fff !important;

        & > div {
            background-color: #fff !important;
        }
    }

    .addressLine {
        border-bottom: 0.6px solid #fff;
    }
`;

const AddressListContainer = styled.div<{ $active: boolean }>`
    cursor: pointer;
    padding: 14px;
    border: 1px solid #0047ff;
    border-radius: 14px;

    background-color: ${({ $active }) => ($active ? "#0047ff" : "#FFFFFF")};
    transition: all 0.2s ease-in-out;

    &:hover {
        border-color: #0047ff;
        background-color: #f0f7ff;
    }

    & > .listBox {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        & > .serial {
            margin-top: 1px;
            background-color: #2275ee;
            color: #ffffff;
            border-radius: 50%;
            min-width: 20px;
            min-height: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 12px;
            font-weight: 400;
            line-height: 20px;
        }

        .useBoxStyle {
            display: flex;
            flex-direction: column;
            font-size: 13px;
            line-height: 20px;
            color: #0047ff;
            border: 1px solid #0047ff;
            border-radius: 4px;
            padding: 0px 4px;
            white-space: nowrap;
            align-items: center;
            & > div {
                width: 100%;
                height: 1px;
                background-color: #0047ff;
            }
        }
    }

    .jibunBox {
        width: 100%;
        text-align: left;
        font-size: 16px;
        font-weight: 400;
        line-height: 23px;
        display: flex;
        flex-direction: row;
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-decoration-color: #0047ff;
        text-underline-offset: 4px;
        &.active {
            text-decoration-color: white;
        }
    }

    .addressLine {
        width: 100%;
        height: 10px;
        border-bottom: 0.6px solid #c9c9c9;
        margin-bottom: 10px;
    }

    .doroTitle {
        align-items: center;
        justify-content: center;
        width: 38px !important;
        height: 20px;
        border-radius: 4px;
        border: 1px solid #fe513f;
        color: #fe513f;
        font-size: 13px;
        font-weight: 400;
        text-align: center;
        padding: 0px 4px;
        white-space: nowrap;
        margin-right: 5px;
    }

    .doroBox {
        width: 100%;
        text-align: left;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        display: flex;
    }

    ${({ $active }) => $active && activeAddressStyle}
`;

const Container = styled.div`
    margin-bottom: 8px;
`;

interface Area {
    square: string;
    pyong: string;
}

interface LandRightInfo {
    [key: string]: any;
}

export interface AddressDetail {
    detail_type: string;
    no: string;
    sub_serial: number;
    type: string;
    address: string;
    address_jibun: string;
    lat: number;
    lng: number;
    pnu: string;
    area: Area;
    shr_sale_flag: boolean;
    shr_sale_info: string;
    detail_info: string;
    land_right_info: LandRightInfo;
    est_price: string;
    est_price_per_square: string;
    official_price_per_square: string;
}

interface GroupedAddressList {
    토지: any[];
    건물: any[];
    집합건물: any[];
    제시외: any[];
    대지권: any[];
    기타: any[];
}
